import type { RadioStation } from '@/types';
import type { NewsItem } from '@/constants/news';
import { fetchStreamsFromAzuraCast } from './azuracast';
import { translateText, normalizeLang } from './translation';
import { STATIONS, saveStationsLkg, loadStationsLkg } from '@/constants/stations';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL || 'http://localhost:1337';

/** Strapi 5: populate je ravni media objekat sa `url`. Legacy: `data` / `attributes`. */
function getMediaUrl(media: unknown): string {
  if (media == null) return '';
  if (typeof media === 'string') {
    return media.startsWith('http') ? media : `${STRAPI_URL}${media}`;
  }
  if (typeof media !== 'object') return '';

  const tryUrl = (obj: { url?: unknown }): string => {
    if (typeof obj.url === 'string') {
      const u = obj.url;
      return u.startsWith('http') ? u : `${STRAPI_URL}${u}`;
    }
    return '';
  };

  const m = media as Record<string, unknown>;

  // Strapi 5 REST — populate=* direktno na polju
  const direct = tryUrl(m as { url?: string });
  if (direct) return direct;

  // { data: Media | null } ili Strapi 4 { data: { attributes } }
  const data = m.data;
  if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    const fromInner = tryUrl(inner as { url?: string });
    if (fromInner) return fromInner;
    const attrs = inner.attributes;
    if (attrs && typeof attrs === 'object') {
      const fromAttrs = tryUrl(attrs as { url?: string });
      if (fromAttrs) return fromAttrs;
    }
  }

  // { attributes: { url } }
  const attrs = m.attributes;
  if (attrs && typeof attrs === 'object') {
    const fromAttrs = tryUrl(attrs as { url?: string });
    if (fromAttrs) return fromAttrs;
  }

  return '';
}

function transformStream(doc: any): RadioStation {
  const attrs = doc.attributes ?? doc;
  const id = doc.documentId ?? doc.id?.toString() ?? attrs.documentId;

  // Prioritet: artworkMedia (Media polje, drag-and-drop u Strapiju) -> artwork (legacy string URL)
  let artworkUrl = '';
  const mediaField = attrs.artworkMedia;
  if (mediaField) {
    artworkUrl = getMediaUrl(mediaField);
  }
  if (!artworkUrl && typeof attrs.artwork === 'string') {
    artworkUrl = attrs.artwork;
  }

  // nameTranslations: očekujemo JSON polje { de: "...", en: "...", ... } iz Strapi-ja.
  // Ako polje ne postoji ili nije objekat, vraćamo prazan objekat (helper ima fallback chain).
  const rawTranslations = attrs.nameTranslations;
  const nameTranslations: Record<string, string> =
    rawTranslations && typeof rawTranslations === 'object' && !Array.isArray(rawTranslations)
      ? Object.fromEntries(
          Object.entries(rawTranslations as Record<string, unknown>).filter(
            ([, v]) => typeof v === 'string' && v.length > 0
          ) as [string, string][]
        )
      : {};

  return {
    id: String(id),
    name: attrs.name ?? '',
    streamUrl: attrs.streamUrl ?? '',
    genre: attrs.genre ?? 'Balkan Mix',
    description: attrs.description ?? '',
    artwork: artworkUrl,
    isActive: attrs.isActive ?? true,
    bitrate: attrs.bitrate ?? 128,
    location: attrs.location ?? '',
    color: attrs.color ?? undefined,
    nameTranslations,
  };
}

/**
 * Prevedi `description` polje u listi stanica na ciljni locale (kroz 7GCOM).
 * Source jezik je uvijek `bs` (na tom se piše u Strapi/CMS i hardcoded STATIONS).
 * Vraća original ako je locale `bs` ili API padne (graceful).
 */
async function translateStations(
  stations: RadioStation[],
  locale: string
): Promise<RadioStation[]> {
  if (normalizeLang(locale) === 'bs' || !locale) return stations;
  return Promise.all(
    stations.map(async (station) => {
      if (!station.description) return station;
      try {
        const translatedDesc = await translateText(station.description, 'bs', locale);
        return { ...station, description: translatedDesc };
      } catch (err) {
        console.warn('[fetchStreams] translateText failed for', station.id, err);
        return station;
      }
    })
  );
}

export async function fetchStreams(locale: string = 'bs'): Promise<RadioStation[]> {
  // 1. Strapi je primarni izvor istine (sve stanice u CMS-u, sa redoslijedom i metadata)
  try {
    const res = await fetch(
      `${STRAPI_URL}/api/streams?filters[isActive][$eq]=true&sort=order:asc&pagination[pageSize]=100&populate=artworkMedia`
    );
    if (res.ok) {
      const json = await res.json();
      const data = json.data ?? json;
      const list = Array.isArray(data) ? data : [data];
      if (list.length > 0) {
        const stations = list.map((d: any) => transformStream(d)).filter(Boolean);
        saveStationsLkg(stations);
        return translateStations(stations, locale);
      }
    }
  } catch (_) {}

  // 2. Fallback — AzuraCast (samo ako Strapi nije dostupan ili je prazan)
  const azuracastStreams = await fetchStreamsFromAzuraCast();
  if (azuracastStreams.length > 0) {
    saveStationsLkg(azuracastStreams);
    return translateStations(azuracastStreams, locale);
  }

  // 3. Last-known-good keš (prethodni uspješan fetch)
  const lkg = loadStationsLkg();
  if (lkg && lkg.length > 0) {
    return translateStations(lkg, locale);
  }

  // 4. Final fallback — hardcoded STATIONS (sa istim prevodom za non-bs)
  return translateStations(STATIONS, locale);
}

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=450&fit=crop';

/**
 * Strapi v5 rich text (Blocks) → Markdown string.
 * Strapi vraća content kao niz blokova:
 *   [{ type: 'paragraph', children: [{ type: 'text', text: '...', bold?, italic? }] }, ...]
 * Podržani blokovi: paragraph, heading (sa `level`), list (`ordered`/`unordered`),
 * quote, code, image, link (kao child sa `type:'link'` + `url`).
 *
 * Ako je input string (npr. Article kolekcija ima Markdown text polje), vraća se kao-je.
 */
function strapiRichTextToMarkdown(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return '';

  const renderInline = (children: unknown): string => {
    if (!Array.isArray(children)) return '';
    return children
      .map((child: any) => {
        if (child == null) return '';
        // Nested link node: { type: 'link', url, children: [...] }
        if (child.type === 'link' && Array.isArray(child.children)) {
          const inner = renderInline(child.children);
          return child.url ? `[${inner}](${child.url})` : inner;
        }
        let text = typeof child.text === 'string' ? child.text : '';
        if (!text) return '';
        if (child.code) text = `\`${text}\``;
        if (child.bold) text = `**${text}**`;
        if (child.italic) text = `*${text}*`;
        if (child.underline) text = `__${text}__`;
        if (child.strikethrough) text = `~~${text}~~`;
        return text;
      })
      .join('');
  };

  return (value as any[])
    .map((block: any) => {
      if (block == null) return '';
      switch (block.type) {
        case 'paragraph':
          return renderInline(block.children);
        case 'heading': {
          const level = Math.min(Math.max(Number(block.level) || 2, 1), 6);
          return `${'#'.repeat(level)} ${renderInline(block.children)}`;
        }
        case 'list': {
          if (!Array.isArray(block.children)) return '';
          const ordered = block.format === 'ordered';
          return block.children
            .map((item: any, idx: number) => {
              const marker = ordered ? `${idx + 1}.` : '-';
              return `${marker} ${renderInline(item?.children)}`;
            })
            .join('\n');
        }
        case 'quote':
          return `> ${renderInline(block.children)}`;
        case 'code': {
          const lang = typeof block.language === 'string' ? block.language : '';
          return `\`\`\`${lang}\n${renderInline(block.children)}\n\`\`\``;
        }
        case 'image': {
          const url = block.image?.url || block.url || '';
          const alt = block.image?.alternativeText || block.alt || '';
          return url ? `![${alt}](${url})` : '';
        }
        default:
          return renderInline(block.children);
      }
    })
    .filter((s: string) => s !== undefined && s !== null)
    .join('\n\n')
    .trim();
}

/** Mapiranje jednog Strapi `news-items` entry-ja u NewsItem (legacy, bez markdown content-a). */
function transformNews(d: any): NewsItem {
  return {
    id: String(d.documentId ?? d.id ?? ''),
    documentId: d.documentId,
    title: d.title ?? '',
    description: d.description ?? '',
    slug: typeof d.slug === 'string' ? d.slug : '',
    publishedAt: d.publishedAt ?? '',
    image: getMediaUrl(d.image) || PLACEHOLDER_IMAGE,
  };
}

/**
 * TASK 31a HOTFIX: pravi shape Programs kolekcije (verifikovan na prod-u 2026-05-01):
 *   { title, subtitle, description (string), excerpt (string|null),
 *     content (Strapi v5 rich text array!), image (media polje, NE coverImage),
 *     slug, publishedAt }
 * `featured` polje NE postoji u trenutnoj Strapi schemi → Boolean(undefined) = false.
 *
 * Excerpt fallback chain: excerpt → description → subtitle → ''.
 * Content fallback chain: rich text → description (string).
 * Image: PRIMARNO `image` (Programs media polje), `coverImage` samo defensive za buduće.
 */
function transformProgram(d: any): NewsItem {
  const excerpt =
    (typeof d.excerpt === 'string' && d.excerpt) ||
    (typeof d.description === 'string' && d.description) ||
    (typeof d.subtitle === 'string' && d.subtitle) ||
    '';

  const content =
    strapiRichTextToMarkdown(d.content) ||
    (typeof d.description === 'string' ? d.description : '');

  return {
    id: String(d.documentId ?? d.id ?? ''),
    documentId: d.documentId,
    title: d.title ?? '',
    description: excerpt,
    excerpt,
    content,
    slug: typeof d.slug === 'string' ? d.slug : '',
    publishedAt: d.publishedAt ?? '',
    featured: Boolean(d.featured),
    image: getMediaUrl(d.image) || getMediaUrl(d.coverImage) || PLACEHOLDER_IMAGE,
  };
}

/**
 * Mapiranje Strapi `articles` entry-ja u NewsItem.
 * Article ima: title, slug, excerpt (string), content (Markdown string),
 * publishedAt, featured (bool), coverImage (media).
 *
 * Defensive guard: ako CMS user prebaci content u Strapi rich text editor,
 * `strapiRichTextToMarkdown` ga konvertuje u Markdown string.
 */
function transformArticle(d: any): NewsItem {
  const excerpt = typeof d.excerpt === 'string' ? d.excerpt : '';
  const content =
    typeof d.content === 'string'
      ? d.content
      : strapiRichTextToMarkdown(d.content);
  return {
    id: String(d.documentId ?? d.id ?? ''),
    documentId: d.documentId,
    title: d.title ?? '',
    description: excerpt,
    excerpt,
    content,
    slug: typeof d.slug === 'string' ? d.slug : '',
    publishedAt: d.publishedAt ?? '',
    featured: Boolean(d.featured),
    image: getMediaUrl(d.coverImage) || getMediaUrl(d.image) || PLACEHOLDER_IMAGE,
  };
}

/**
 * Prevod cijelog Article-a (title, excerpt, content) na ciljni locale
 * preko OpenAI proxy-ja. Šaljemo cijeli markdown jednom — OpenAI razumije
 * Markdown strukturu i čuva formatting (više nema paragraph-by-paragraph).
 */
async function translateArticleViaOpenAI(
  item: NewsItem,
  targetLocale: string
): Promise<NewsItem> {
  if (targetLocale === 'bs' || !targetLocale) return item;

  const excerptSource = item.excerpt || item.description || '';
  const [translatedTitle, translatedExcerpt, translatedContent] = await Promise.all([
    item.title ? translateText(item.title, 'bs', targetLocale) : '',
    excerptSource ? translateText(excerptSource, 'bs', targetLocale) : '',
    item.content ? translateText(item.content, 'bs', targetLocale) : '',
  ]);

  return {
    ...item,
    title: translatedTitle,
    description: translatedExcerpt,
    excerpt: translatedExcerpt,
    content: translatedContent,
  };
}

/**
 * Prevedi excerpt polja (title + description) NewsItem-a kroz 7GCOM.
 * NE prevodi `content` (Blocks JSON) — full body se piše ručno u Strapi
 * na ciljnom jeziku jer je marketing-relevantan i ne smije se mašinski lomiti.
 */
async function translateNewsItems(items: NewsItem[], locale: string): Promise<NewsItem[]> {
  if (locale === 'bs' || !locale) return items;
  return Promise.all(
    items.map(async (item) => ({
      ...item,
      title: await translateText(item.title, 'bs', locale).catch(() => item.title),
      description: item.description
        ? await translateText(item.description, 'bs', locale).catch(() => item.description)
        : item.description,
    }))
  );
}


/** Vesti iz Strapija – locale za i18n (de, en, bs, hr, mk, sr-Cyrl, sr) */
export async function fetchNews(locale = 'de'): Promise<NewsItem[]> {
  // Strategija: uvijek vuci iz 'bs' (jezik na kojem se pise u CMS-u),
  // a u runtime prevedi excerpt (title/description) kroz 7GCOM. `content` ostaje original.
  try {
    const res = await fetch(
      `${STRAPI_URL}/api/news-items?locale=bs&status=published&sort=publishedAt:desc&pagination[pageSize]=100&populate=image`
    );
    if (!res.ok) return [];
    const json = await res.json();
    const data = json.data ?? json;
    const list = Array.isArray(data) ? data : data ? [data] : [];
    const items = list.map(transformNews).filter((n: NewsItem) => n.title);

    if (items.length === 0) return [];
    return translateNewsItems(items, locale);
  } catch {
    return [];
  }
}

/** Single news entry by slug — za detail view. */
export async function fetchNewsBySlug(slug: string, locale = 'de'): Promise<NewsItem | null> {
  if (!slug) return null;
  try {
    // Pokušaj prvo locale=ciljni (jer Strapi već sadrži lokalizovan content).
    // Ako prazno, fallback na bs i (samo) excerpt prevedi.
    let res = await fetch(
      `${STRAPI_URL}/api/news-items?locale=${encodeURIComponent(locale)}&filters[slug][$eq]=${encodeURIComponent(slug)}&populate=image`
    );
    let json = res.ok ? await res.json() : { data: [] };
    let list = Array.isArray(json.data) ? json.data : [];

    if (list.length === 0) {
      // Fallback: bs locale pa prevod excerpt-a
      res = await fetch(
        `${STRAPI_URL}/api/news-items?locale=bs&filters[slug][$eq]=${encodeURIComponent(slug)}&populate=image`
      );
      if (!res.ok) return null;
      json = await res.json();
      list = Array.isArray(json.data) ? json.data : [];
      if (list.length === 0) return null;

      const item = transformNews(list[0]);
      const [translated] = await translateNewsItems([item], locale);
      return translated;
    }

    return transformNews(list[0]);
  } catch (err) {
    console.warn('[fetchNewsBySlug] error:', err);
    return null;
  }
}

/**
 * Programi iz Strapi `programs` content type-a (TASK 31 — mirror Article logike).
 * Smart Locale Fetching: ciljni locale → fallback bs + OpenAI runtime prevod.
 *
 * Vraća `NewsItem[]` da se može tretirati ravno sa Articles (featured banner +
 * ArticleCard koriste isti shape).
 */
export async function fetchProgram(locale: string = 'bs'): Promise<NewsItem[]> {
  // Pokušaj 1: direktan fetch za ciljni locale (ručno preveden u Strapi-ju)
  if (locale !== 'bs' && locale) {
    try {
      const url = `${STRAPI_URL}/api/programs?locale=${encodeURIComponent(locale)}&status=published&sort=publishedAt:desc&pagination[pageSize]=100&populate=image`;
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        const list = Array.isArray(json.data) ? json.data : [];
        if (list.length > 0) {
          const items = list.map(transformProgram).filter((p: NewsItem) => p.title);
          console.log(
            `[fetchProgram] Found ${items.length} programs in '${locale}' (manual translation in Strapi)`
          );
          return items;
        }
      }
    } catch (err) {
      console.warn(`[fetchProgram] direct ${locale} fetch failed:`, err);
    }
  }

  // Pokušaj 2: fallback na `bs` + OpenAI runtime prevod
  try {
    const url = `${STRAPI_URL}/api/programs?locale=bs&status=published&sort=publishedAt:desc&pagination[pageSize]=100&populate=image`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const json = await res.json();
    const list = Array.isArray(json.data) ? json.data : [];
    let items: NewsItem[] = list.map(transformProgram).filter((p: NewsItem) => p.title);

    if (items.length === 0) return [];
    if (locale === 'bs' || !locale) return items;

    console.log(
      `[fetchProgram] Translating ${items.length} programs to '${locale}' via OpenAI proxy`
    );
    items = await Promise.all(
      items.map((item: NewsItem) => translateArticleViaOpenAI(item, locale))
    );
    return items;
  } catch (err) {
    console.warn('[fetchProgram] fallback error:', err);
    return [];
  }
}

/**
 * Single program entry po slug-u (TASK 31 — Article mirror).
 * Smart Locale Fetching + OpenAI fallback prevod.
 */
export async function fetchProgramBySlug(
  slug: string,
  locale: string = 'bs'
): Promise<NewsItem | null> {
  if (!slug) return null;

  // Pokušaj 1a: direktan fetch za ciljni locale
  if (locale !== 'bs' && locale) {
    try {
      const url = `${STRAPI_URL}/api/programs?locale=${encodeURIComponent(locale)}&filters[slug][$eq]=${encodeURIComponent(slug)}&populate=image`;
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        const list = Array.isArray(json.data) ? json.data : [];
        if (list.length > 0) {
          return transformProgram(list[0]);
        }
      }
    } catch (err) {
      console.warn(`[fetchProgramBySlug] direct ${locale} fetch failed:`, err);
    }
  }

  // Pokušaj 1b: bs + OpenAI prevod
  try {
    const url = `${STRAPI_URL}/api/programs?locale=bs&filters[slug][$eq]=${encodeURIComponent(slug)}&populate=image`;
    const res = await fetch(url);
    if (res.ok) {
      const json = await res.json();
      const list = Array.isArray(json.data) ? json.data : [];
      if (list.length > 0) {
        const item = transformProgram(list[0]);
        if (locale === 'bs' || !locale) return item;
        return translateArticleViaOpenAI(item, locale);
      }
    }
  } catch (err) {
    console.warn('[fetchProgramBySlug] bs fallback error:', err);
  }

  // Pokušaj 2: fallback po documentId / id (slug nije postavljen)
  try {
    const filters =
      `filters[$or][0][documentId][$eq]=${encodeURIComponent(slug)}` +
      `&filters[$or][1][id][$eq]=${encodeURIComponent(slug)}`;
    const url = `${STRAPI_URL}/api/programs?locale=bs&${filters}&populate=image`;
    const res = await fetch(url);
    if (res.ok) {
      const json = await res.json();
      const list = Array.isArray(json.data) ? json.data : [];
      if (list.length > 0) {
        const item = transformProgram(list[0]);
        if (locale === 'bs' || !locale) return item;
        return translateArticleViaOpenAI(item, locale);
      }
    }
  } catch (err) {
    console.warn('[fetchProgramBySlug] documentId/id fallback error:', err);
  }

  return null;
}

// ============================================================================
// Articles (TASK 23b + 30) — primarni izvor za "Vesti" sekciju.
// Strapi `articles` content type: title, slug (UID), excerpt, content (markdown),
// publishedAt, featured (bool), coverImage (media). i18n na bs locale-u u CMS-u.
//
// Smart Locale Fetching (TASK 28):
//   1. Pokušaj direktno fetchovati za traženi locale (ručno preveden u Strapi-ju).
//   2. Ako prazno, fallback na `bs` + runtime prevod kroz OpenAI proxy.
// ============================================================================

/** Vesti iz Strapi `articles` content type-a. */
export async function fetchArticles(locale: string = 'bs'): Promise<NewsItem[]> {
  // Pokušaj 1: direktan fetch za ciljni locale (ručno preveden sadržaj u CMS-u)
  if (locale !== 'bs' && locale) {
    try {
      const url = `${STRAPI_URL}/api/articles?locale=${encodeURIComponent(locale)}&status=published&sort=publishedAt:desc&pagination[pageSize]=100&populate=coverImage`;
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        const list = Array.isArray(json.data) ? json.data : [];
        if (list.length > 0) {
          const items = list.map(transformArticle).filter((a: NewsItem) => a.title);
          console.log(
            `[fetchArticles] Found ${items.length} articles in '${locale}' (manual translation in Strapi)`
          );
          return items;
        }
      }
    } catch (err) {
      console.warn(`[fetchArticles] direct ${locale} fetch failed:`, err);
    }
  }

  // Pokušaj 2: fallback na `bs` + OpenAI runtime prevod
  try {
    const url = `${STRAPI_URL}/api/articles?locale=bs&status=published&sort=publishedAt:desc&pagination[pageSize]=100&populate=coverImage`;
    const res = await fetch(url);
    if (!res.ok) {
      console.warn('[fetchArticles] HTTP', res.status);
      return [];
    }
    const json = await res.json();
    const list = Array.isArray(json.data) ? json.data : [];
    let items: NewsItem[] = list.map(transformArticle).filter((a: NewsItem) => a.title);

    if (items.length === 0) return [];
    if (locale === 'bs' || !locale) return items;

    console.log(
      `[fetchArticles] Translating ${items.length} articles to '${locale}' via OpenAI proxy`
    );
    items = await Promise.all(
      items.map((item: NewsItem) => translateArticleViaOpenAI(item, locale))
    );
    return items;
  } catch (err) {
    console.warn('[fetchArticles] fallback error:', err);
    return [];
  }
}

// ----------------------------------------------------------------------------
// Legacy fallback helpers (TASK 25) — koriste se samo iz `fetchArticleBySlug`
// kad Articles kolekcija nema entry sa traženim slug-om/id-om. Dijelovi NewsScreen-a
// koji direktno linkuju na `/news/:slug` ili `/program/:slug` (preko id-a kad
// slug nije postavljen u Strapi-ju) idu kroz isti detail view i tu se rješavaju.
// ----------------------------------------------------------------------------

/** Pokušaj iz `news-items` kolekcije po slug-u, documentId-u ili numeričkom id-u. */
async function fetchLegacyNewsItem(idOrSlug: string, locale: string): Promise<NewsItem | null> {
  try {
    const filters =
      `filters[$or][0][slug][$eq]=${encodeURIComponent(idOrSlug)}` +
      `&filters[$or][1][documentId][$eq]=${encodeURIComponent(idOrSlug)}` +
      `&filters[$or][2][id][$eq]=${encodeURIComponent(idOrSlug)}`;
    const url = `${STRAPI_URL}/api/news-items?locale=bs&${filters}&populate=image`;
    const res = await fetch(url);
    if (!res.ok) return null;

    const json = await res.json();
    const list = Array.isArray(json.data) ? json.data : [];
    if (list.length === 0) return null;

    const d = list[0];
    const description = d.description ?? '';
    let item: NewsItem = {
      id: String(d.documentId ?? d.id ?? ''),
      documentId: d.documentId,
      title: d.title ?? '',
      description,
      // Legacy news-items nemaju odvojen body content — koristimo description kao content.
      excerpt: description,
      content: description,
      slug: d.slug || String(d.documentId ?? d.id ?? ''),
      publishedAt: d.publishedAt ?? '',
      image: getMediaUrl(d.image) || PLACEHOLDER_IMAGE,
    };

    if (locale !== 'bs' && item.title) {
      item = await translateArticleViaOpenAI(item, locale);
    }
    return item;
  } catch (err) {
    console.warn('[fetchLegacyNewsItem] error:', err);
    return null;
  }
}

/** Pokušaj iz `programs` kolekcije — content se sastavlja kao markdown (subtitle bold + description). */
async function fetchLegacyProgram(idOrSlug: string, locale: string): Promise<NewsItem | null> {
  try {
    const filters =
      `filters[$or][0][slug][$eq]=${encodeURIComponent(idOrSlug)}` +
      `&filters[$or][1][documentId][$eq]=${encodeURIComponent(idOrSlug)}` +
      `&filters[$or][2][id][$eq]=${encodeURIComponent(idOrSlug)}`;
    const url = `${STRAPI_URL}/api/programs?locale=bs&${filters}&populate=image`;
    const res = await fetch(url);
    if (!res.ok) return null;

    const json = await res.json();
    const list = Array.isArray(json.data) ? json.data : [];
    if (list.length === 0) return null;

    const d = list[0];
    const description = d.description ?? '';
    const subtitle = d.subtitle ?? '';
    const composedContent = [subtitle ? `**${subtitle}**` : '', description]
      .filter(Boolean)
      .join('\n\n');

    let item: NewsItem = {
      id: String(d.documentId ?? d.id ?? ''),
      documentId: d.documentId,
      title: d.title ?? '',
      description,
      excerpt: description,
      content: composedContent,
      slug: d.slug || String(d.documentId ?? d.id ?? ''),
      publishedAt: d.publishedAt ?? '',
      image: getMediaUrl(d.image) || PLACEHOLDER_IMAGE,
    };

    if (locale !== 'bs' && item.title) {
      item = await translateArticleViaOpenAI(item, locale);
    }
    return item;
  } catch (err) {
    console.warn('[fetchLegacyProgram] error:', err);
    return null;
  }
}

/**
 * Single article po slug-u (za /article/:slug, /news/:slug, /program/:slug detail view).
 *
 * Smart Locale Fetching (TASK 28) + fallback chain (TASK 25 + 30):
 *   1a. Direktan fetch za traženi locale (ručno preveden u Strapi-ju)
 *   1b. /api/articles?locale=bs&filters[slug][$eq]=... + OpenAI prevod
 *   2.  /api/articles?filters[$or][documentId|id][$eq]=...  (Articles bez slug-a)
 *   3.  /api/news-items?filters[$or][slug|documentId|id][$eq]=...  (legacy news)
 *   4.  /api/programs?filters[$or][slug|documentId|id][$eq]=...  (legacy program)
 *   5.  null
 *
 * Tako da legacy linkovi i dalje rade nakon migracije na Articles content type.
 */
export async function fetchArticleBySlug(
  slug: string,
  locale: string = 'bs'
): Promise<NewsItem | null> {
  if (!slug) return null;

  // 1a) Articles po slug-u — direktan ciljni locale (ručno prevedeno u Strapi-ju)
  if (locale !== 'bs' && locale) {
    try {
      const url = `${STRAPI_URL}/api/articles?locale=${encodeURIComponent(locale)}&filters[slug][$eq]=${encodeURIComponent(slug)}&populate=coverImage`;
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        const list = Array.isArray(json.data) ? json.data : [];
        if (list.length > 0) {
          return transformArticle(list[0]);
        }
      }
    } catch (err) {
      console.warn(`[fetchArticleBySlug] direct ${locale} fetch failed:`, err);
    }
  }

  // 1b) Articles po slug-u — bs + OpenAI prevod
  try {
    const url = `${STRAPI_URL}/api/articles?locale=bs&filters[slug][$eq]=${encodeURIComponent(slug)}&populate=coverImage`;
    const res = await fetch(url);
    if (res.ok) {
      const json = await res.json();
      const list = Array.isArray(json.data) ? json.data : [];
      if (list.length > 0) {
        const item = transformArticle(list[0]);
        if (locale === 'bs' || !locale) return item;
        return translateArticleViaOpenAI(item, locale);
      }
    }
  } catch (err) {
    console.warn('[fetchArticleBySlug] Articles by slug error:', err);
  }

  // 2) Articles po documentId/id (ako article nema postavljen slug)
  try {
    const filters =
      `filters[$or][0][documentId][$eq]=${encodeURIComponent(slug)}` +
      `&filters[$or][1][id][$eq]=${encodeURIComponent(slug)}`;
    const url = `${STRAPI_URL}/api/articles?locale=bs&${filters}&populate=coverImage`;
    const res = await fetch(url);
    if (res.ok) {
      const json = await res.json();
      const list = Array.isArray(json.data) ? json.data : [];
      if (list.length > 0) {
        const item = transformArticle(list[0]);
        if (locale === 'bs' || !locale) return item;
        return translateArticleViaOpenAI(item, locale);
      }
    }
  } catch (err) {
    console.warn('[fetchArticleBySlug] Articles by id error:', err);
  }

  // 3) news-items legacy fallback
  const legacyNews = await fetchLegacyNewsItem(slug, locale);
  if (legacyNews) return legacyNews;

  // 4) programs legacy fallback
  const legacyProgram = await fetchLegacyProgram(slug, locale);
  if (legacyProgram) return legacyProgram;

  return null;
}
